"use client";

import { c as _c } from "react/compiler-runtime";
import { IconMessageCircleFilled } from "@tabler/icons-react";
import { formatForDisplay } from "@tanstack/react-hotkeys";
import { Button } from "@domainstack/ui/button";
import { Separator } from "@domainstack/ui/separator";
import { Spinner } from "@domainstack/ui/spinner";
import { cn } from "@domainstack/ui/utils";
export const CHAT_HOTKEY = "Mod+I";
interface ChatFabProps {
  loading?: boolean;
  onClick: () => void;
  /** Hover/focus intent, so the chat bundle can start loading before the click. */
  onPrefetch?: () => void;
}
interface ChatFabButtonProps extends Required<Omit<ChatFabProps, "onPrefetch">> {
  onPrefetch?: () => void;
  children: React.ReactNode;
  className: string;
  size: "default" | "icon-lg";
}
function ChatFabButton(t0) {
  const $ = _c(9);
  const {
    children,
    className,
    loading,
    onClick,
    onPrefetch,
    size
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("animate-in duration-250 ease-[cubic-bezier(0.22,1,0.36,1)] fade-in-0 slide-in-from-bottom-4 motion-reduce:animate-none", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== loading || $[4] !== onClick || $[5] !== onPrefetch || $[6] !== size || $[7] !== t1) {
    t2 = <Button variant="outline" size={size} aria-label="Ask AI" aria-busy={loading} disabled={loading} className={t1} onClick={onClick} onPointerEnter={onPrefetch} onFocus={onPrefetch}>{children}</Button>;
    $[2] = children;
    $[3] = loading;
    $[4] = onClick;
    $[5] = onPrefetch;
    $[6] = size;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

// Static: the gradient itself never changes, so it never needs repainting.
// Rendered oversized and drifted with a CSS animation on transforms (compositor-only) that only
// plays while hovered, instead of animating the `background` positions directly.
const MESH_GRADIENT_BACKGROUND = `
  radial-gradient(ellipse at 25% 30%, rgba(56, 189, 248, 0.8) 0%, transparent 50%),
  radial-gradient(ellipse at 75% 65%, rgba(139, 92, 246, 0.7) 0%, transparent 55%),
  radial-gradient(ellipse at 55% 20%, rgba(236, 72, 153, 0.6) 0%, transparent 45%),
  radial-gradient(ellipse at 40% 80%, rgba(34, 197, 94, 0.5) 0%, transparent 50%)
`;
function MobileChatFab(t0) {
  const $ = _c(7);
  const {
    loading,
    onClick,
    onPrefetch
  } = t0;
  let t1;
  if ($[0] !== loading) {
    t1 = <span className="relative z-10 flex items-center justify-center">{loading ? <Spinner className="size-4 text-foreground/90" /> : <IconMessageCircleFilled className="size-4 text-foreground/90" aria-hidden="true" />}</span>;
    $[0] = loading;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== loading || $[3] !== onClick || $[4] !== onPrefetch || $[5] !== t1) {
    t2 = <ChatFabButton loading={loading} onClick={onClick} onPrefetch={onPrefetch} size="icon-lg" className="group fixed right-6 bottom-6 z-40 overflow-hidden rounded-full shadow-lg backdrop-blur-md transition-none md:hidden">{t1}</ChatFabButton>;
    $[2] = loading;
    $[3] = onClick;
    $[4] = onPrefetch;
    $[5] = t1;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
}
function DesktopChatFab(t0) {
  const $ = _c(8);
  const {
    loading,
    onClick,
    onPrefetch
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span data-slot="mesh-gradient" className="pointer-events-none absolute inset-[-50%] animate-mesh-drift rounded-[inherit] opacity-0 blur-[4px] transition-opacity duration-200 group-hover:opacity-100 group-hover:[animation-play-state:running] motion-reduce:hidden" style={{
      background: MESH_GRADIENT_BACKGROUND
    }} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <span className="text-[13px] leading-none font-semibold tracking-tight">Ask AI</span>;
    t3 = <Separator orientation="vertical" className="bg-border/80 group-hover:bg-foreground/40 data-[orientation=vertical]:h-4" />;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="relative z-10 flex items-center gap-2">{t2}{t3}<kbd className="pointer-events-none font-sans text-xs leading-none font-medium text-foreground/70 select-none">{formatForDisplay(CHAT_HOTKEY, {
          separatorToken: "\xA0"
        })}</kbd></span>;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== loading || $[5] !== onClick || $[6] !== onPrefetch) {
    t5 = <ChatFabButton loading={loading} onClick={onClick} onPrefetch={onPrefetch} size="default" className="group fixed right-6 bottom-6 z-40 hidden h-8 overflow-hidden px-2.5 shadow-lg backdrop-blur-md transition-none md:flex">{t1}{t4}</ChatFabButton>;
    $[4] = loading;
    $[5] = onClick;
    $[6] = onPrefetch;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}
export function ChatFab(t0) {
  const $ = _c(4);
  const {
    loading: t1,
    onClick,
    onPrefetch
  } = t0;
  const loading = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== loading || $[1] !== onClick || $[2] !== onPrefetch) {
    t2 = <><MobileChatFab loading={loading} onClick={onClick} onPrefetch={onPrefetch} /><DesktopChatFab loading={loading} onClick={onClick} onPrefetch={onPrefetch} /></>;
    $[0] = loading;
    $[1] = onClick;
    $[2] = onPrefetch;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}